'use client';

import Link from '@eliadmin/core/src/components/link';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { MdOutlineShoppingCart } from 'react-icons/md';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function BestProducts({ locale, title }: { locale: string; title: string }) {
  const me = useCustomerStore(s => s.me);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);

  const productsQueries = buildProductsQuery({
    order: 'desc',
    orderBy: 'views',
    results: 6,
    page: 1,
  });

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, 'best', locale, me?.authToken ?? null],
    queryFn: async ({ signal }) => {
      return await fetchProducts({
        locale,
        authToken: me?.authToken,
        signal,
        query: {
          ...productsQueries,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
    enabled: isCustomerInitialized,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = !isCustomerInitialized || productsQuery.isLoading;

  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'ea-section-big__top',
        'ea-section-medium__bottom',
        'pr-0 sm:pr-4',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-8 sm:gap-12 lg:gap-16',
        )}
      >
        <div className="flex w-full items-end justify-between">
          <Heading subtitle="E-SHOP" title={title} />
          <Button asChild className="hidden sm:flex" variant={'secondary'}>
            <Link prefetch={false} href={'/eshop'}>
              <MdOutlineShoppingCart size={20} /> {t('text3319')}
            </Link>
          </Button>
        </div>
        <div className="grid w-full snap-x snap-mandatory auto-cols-[290px] grid-flow-col gap-4 overflow-x-auto overscroll-x-contain pb-2 sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:gap-x-6 sm:gap-y-8 sm:overflow-visible sm:pb-0 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 6 }, (_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="min-w-0 snap-start motion-safe:animate-pulse"
                >
                  <div className="aspect-square w-full rounded-[24px] bg-neutral-100" />
                  <div className="mt-4 h-12 rounded bg-neutral-100" />
                  <div className="mt-4 h-7 w-24 rounded bg-neutral-100" />
                </div>
              ))
            : products.map(product => (
                <div key={product.slug} className="min-w-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
        </div>
        <div className="w-full pr-4">
          <Button asChild className="flex sm:hidden" variant={'secondary'}>
            <Link prefetch={false} href={'/eshop'}>
              <MdOutlineShoppingCart size={20} /> {t('text3319')}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
